import { NaturezaAnalise } from "@/@types/NaturezaAnalise";
import { IFormValuesCertificadoAnalise } from "@/interfaces/IFormValuesCertificadoAnalise";
import { IProdutoAutorizado } from "@/interfaces/IProdutoAutorizado";
import { ModalHistoricoAnalise } from "@/pages/analiseLaboratorial/_components/ModalHistoricoAnalise";
import { OrderedListOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  FormInstance,
  Grid,
  Input,
  InputNumber,
  Row,
  Table,
  Typography,
} from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import styles from "./style.module.css";

interface FormTabelaEnsaiosProps {
  form: FormInstance<IFormValuesCertificadoAnalise>;
  produto?: IProdutoAutorizado | null;
  estabelecimentoId: number;
  naturezaAnalise: NaturezaAnalise;
  exigeResultadoUmidade?: boolean;
  isAgua?: boolean;
}

export function FormTabelaEnsaios({
  form,
  produto,
  estabelecimentoId,
  naturezaAnalise,
  exigeResultadoUmidade = false,
  isAgua = false,
}: FormTabelaEnsaiosProps) {
  const screens = Grid.useBreakpoint();
  const amostraImpropria = Form.useWatch("amostraImpropria", form);
  const ensaiosCertificado = Form.useWatch("ensaiosCertificado", form) || [];
  const resultadoUmidade = Form.useWatch("resultadoUmidade", form);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const handleOpenModalHistorico = () => {
    setIsOpenModal(true);
  };

  const handleCloseModalHistorico = () => {
    setIsOpenModal(false);
  };

  const handleResultadoChange = (ensaioId: number, value: number | null) => {
    if (amostraImpropria) return; // Bloquear edição se amostra for imprópria

    // Atualização imutável do array, garantindo key única
    const novosDados = ensaiosCertificado.map((ensaio, idx) => {
      if (ensaio.ensaio.id !== ensaioId)
        return { ...ensaio, key: ensaio.key ?? idx };

      // Busca o ensaioProduto correspondente
      const ensaioProduto = produto?.produto.ensaios.find(
        (e) => e.ensaio.id === ensaioId
      );

      // Função para verificar conformidade
      const isConforme = (resultado: number): boolean => {
        if (!ensaioProduto) return false;
        const hasResultadoUmidade =
          resultadoUmidade !== undefined && resultadoUmidade !== null;

        if (exigeResultadoUmidade && !hasResultadoUmidade) return false;
        if (hasResultadoUmidade) {
          if (resultadoUmidade >= 0 && resultadoUmidade < 36) {
            return getResultadoValido(
              resultado,
              Number(ensaioProduto.refInicial0a36),
              Number(ensaioProduto.refFinal0a36)
            );
          }
          if (resultadoUmidade >= 36 && resultadoUmidade < 46) {
            return getResultadoValido(
              resultado,
              Number(ensaioProduto.refInicial36a46),
              Number(ensaioProduto.refFinal36a46)
            );
          }
          // Para valores >= 46
          return getResultadoValido(
            resultado,
            Number(ensaioProduto.refInicial46a55),
            Number(ensaioProduto.refFinal46a55)
          );
        }
        // Se não tem resultado de umidade, usa referências gerais
        return getResultadoValido(
          resultado,
          Number(ensaioProduto.referenciaInicial),
          Number(ensaioProduto.referenciaFinal)
        );
      };

      if (value === null) {
        return {
          ...ensaio,
          resultado: undefined,
          conforme: isAgua ? ensaio.conforme : false,
          naoRealizada: false,
          key: ensaio.key ?? idx,
        };
      }

      return {
        ...ensaio,
        resultado: value,
        conforme: isAgua ? ensaio.conforme : isConforme(value),
        naoRealizada: false,
        key: ensaio.key ?? idx,
      };
    });
    form.setFieldsValue({ ensaiosCertificado: novosDados });
  };

  const handleNaoRealizadoChange = (ensaioId: number, checked: boolean) => {
    if (amostraImpropria) return; // Bloquear edição se amostra for imprópria

    const novosDados = ensaiosCertificado.map((ensaio, idx) =>
      ensaio.ensaio.id === ensaioId
        ? {
            ...ensaio,
            naoRealizada: checked,
            conforme: false,
            resultado: undefined,
            key: ensaio.key ?? idx,
          }
        : { ...ensaio, key: ensaio.key ?? idx }
    );
    form.setFieldsValue({ ensaiosCertificado: novosDados });
  };

  const handleConformeChange = (ensaioId: number, checked: boolean) => {
    if (amostraImpropria || !isAgua) return;

    const novosDados = ensaiosCertificado.map((ensaio, idx) =>
      ensaio.ensaio.id === ensaioId
        ? {
            ...ensaio,
            conforme: checked,
            key: ensaio.key ?? idx,
          }
        : { ...ensaio, key: ensaio.key ?? idx }
    );

    form.setFieldsValue({ ensaiosCertificado: novosDados });
  };

  const getResultadoValido = (
    value: number,
    refInicial: number,
    refFinal: number
  ): boolean => {
    return value >= refInicial && value <= refFinal;
  };

  const handleChangeResultadoUmidade = (valueUmidade: number | null) => {
    if (isAgua) return;

    const isConforme = (resultado: number, ensaioId: number) => {
      const ensaioProduto = produto?.produto.ensaios.find(
        (e) => e.ensaio.id === ensaioId
      );
      if (!ensaioProduto || valueUmidade === null) return false;
      if (valueUmidade >= 0 && valueUmidade < 36) {
        return getResultadoValido(
          resultado,
          Number(ensaioProduto.refInicial0a36),
          Number(ensaioProduto.refFinal0a36)
        );
      } else if (valueUmidade >= 36 && valueUmidade < 46) {
        return getResultadoValido(
          resultado,
          Number(ensaioProduto.refInicial36a46),
          Number(ensaioProduto.refFinal36a46)
        );
      } else if (valueUmidade >= 46 && valueUmidade <= 55) {
        return getResultadoValido(
          resultado,
          Number(ensaioProduto.refInicial46a55),
          Number(ensaioProduto.refFinal46a55)
        );
      } else {
        return false;
      }
    };

    const novosDados = ensaiosCertificado.map((ensaio, idx) => {
      if (ensaio.resultado === undefined)
        return { ...ensaio, key: ensaio.key ?? idx };
      return {
        ...ensaio,
        conforme: isConforme(ensaio.resultado, ensaio.ensaio.id),
        key: ensaio.key ?? idx,
      };
    });

    form.setFieldsValue({ ensaiosCertificado: novosDados });
  };

  const columns: ColumnsType<
    IFormValuesCertificadoAnalise["ensaiosCertificado"][number]
  > = [
    {
      title: "Ensaio",
      dataIndex: "ensaio",
      key: "ensaio",
      ellipsis: true,
      align: "center",
      render: (_, record) => `${record.ensaio.codigo} - ${record.ensaio.nome}`,
    },
    {
      title: "Padrão Referência",
      dataIndex: "padraoReferencia",
      key: "padraoReferencia",
      align: "center",
      render: (_, record) => record.ensaio.padraoReferencia || "-",
    },
    {
      title: "Referência Geral",
      align: "center",
      children: [
        {
          title: "Início",
          dataIndex: "referenciaInicial",
          key: "referenciaInicial",
          align: "center",
          render: (_, record) => {
            const ensaioProduto = produto?.produto.ensaios.find(
              (e) => e.ensaio.id === record.ensaio.id
            );

            if (
              ensaioProduto?.referenciaInicial === undefined ||
              ensaioProduto?.referenciaInicial === null
            ) {
              return "-";
            }
            return ensaioProduto.referenciaInicial;
          },
        },
        {
          title: "Fim",
          dataIndex: "referenciaFinal",
          key: "referenciaFinal",
          align: "center",
          render: (_, record) => {
            const ensaioProduto = produto?.produto.ensaios.find(
              (e) => e.ensaio.id === record.ensaio.id
            );
            if (
              ensaioProduto?.referenciaFinal === undefined ||
              ensaioProduto?.referenciaFinal === null
            ) {
              return "-";
            }
            return ensaioProduto.referenciaFinal;
          },
        },
      ],
    },
    {
      title: "0 a 36",
      align: "center",
      children: [
        {
          title: "Início",
          dataIndex: "refInicial0a36",
          key: "refInicial0a36",
          align: "center",
          render: (_, record) => {
            const ensaioProduto = produto?.produto.ensaios.find(
              (e) => e.ensaio.id === record.ensaio.id
            );
            if (
              ensaioProduto?.refInicial0a36 === undefined ||
              ensaioProduto?.refInicial0a36 === null
            ) {
              return "-";
            }
            return ensaioProduto.refInicial0a36;
          },
        },
        {
          title: "Fim",
          dataIndex: "refFinal0a36",
          key: "refFinal0a36",
          align: "center",
          render: (_, record) => {
            const ensaioProduto = produto?.produto.ensaios.find(
              (e) => e.ensaio.id === record.ensaio.id
            );
            if (
              ensaioProduto?.refFinal0a36 === undefined ||
              ensaioProduto?.refFinal0a36 === null
            ) {
              return "-";
            }
            return ensaioProduto.refFinal0a36;
          },
        },
      ],
    },
    {
      title: "36 a 46",
      align: "center",
      children: [
        {
          title: "Início",
          dataIndex: "refInicial36a46",
          key: "refInicial36a46",
          align: "center",
          render: (_, record) => {
            const ensaioProduto = produto?.produto.ensaios.find(
              (e) => e.ensaio.id === record.ensaio.id
            );
            if (
              ensaioProduto?.refInicial36a46 === undefined ||
              ensaioProduto?.refInicial36a46 === null
            ) {
              return "-";
            }
            return ensaioProduto.refInicial36a46;
          },
        },
        {
          title: "Fim",
          dataIndex: "refFinal36a46",
          key: "refFinal36a46",
          align: "center",
          render: (_, record) => {
            const ensaioProduto = produto?.produto.ensaios.find(
              (e) => e.ensaio.id === record.ensaio.id
            );
            if (
              ensaioProduto?.refFinal36a46 === undefined ||
              ensaioProduto?.refFinal36a46 === null
            ) {
              return "-";
            }
            return ensaioProduto.refFinal36a46;
          },
        },
      ],
    },
    {
      title: "46 a 55",
      align: "center",
      children: [
        {
          title: "Início",
          dataIndex: "refInicial46a55",
          key: "refInicial46a55",
          align: "center",
          render: (_, record) => {
            const ensaioProduto = produto?.produto.ensaios.find(
              (e) => e.ensaio.id === record.ensaio.id
            );
            if (
              ensaioProduto?.refInicial46a55 === undefined ||
              ensaioProduto?.refInicial46a55 === null
            ) {
              return "-";
            }
            return ensaioProduto.refInicial46a55;
          },
        },
        {
          title: "Fim",
          dataIndex: "refFinal46a55",
          key: "refFinal46a55",
          align: "center",
          render: (_, record) => {
            const ensaioProduto = produto?.produto.ensaios.find(
              (e) => e.ensaio.id === record.ensaio.id
            );
            if (
              ensaioProduto?.refFinal46a55 === undefined ||
              ensaioProduto?.refFinal46a55 === null
            ) {
              return "-";
            }
            return ensaioProduto.refFinal46a55;
          },
        },
      ],
    },
    {
      title: "Resultado",
      key: "resultado",
      align: "center",
      render: (_, record) => (
        <InputNumber
          style={{ width: "100%" }}
          value={record.resultado}
          onChange={(value) => handleResultadoChange(record.ensaio.id, value)}
          disabled={amostraImpropria}
          placeholder="Informe o resultado"
          decimalSeparator=","
        />
      ),
    },
    {
      title: "Unidade",
      dataIndex: ["ensaio", "unidadeMedida", "sigla"],
      key: "unidade",
      align: "center",
    },
    {
      title: "Conforme",
      key: "conforme",
      align: "center",
      render: (_, record) => (
        <Checkbox
          checked={record.conforme}
          disabled={amostraImpropria || record.naoRealizada || !isAgua}
          onChange={(e) =>
            handleConformeChange(record.ensaio.id, e.target.checked)
          }
        />
      ),
    },
    {
      title: "Não Realizada",
      key: "naoRealizada",
      align: "center",
      render: (_, record) => (
        <Checkbox
          checked={record.naoRealizada}
          onChange={(e) =>
            handleNaoRealizadoChange(record.ensaio.id, e.target.checked)
          }
          disabled={amostraImpropria}
        />
      ),
    },
  ];

  useEffect(() => {
    if (amostraImpropria) {
      const ensaiosLimpos = ensaiosCertificado.map((ensaio) => ({
        ...ensaio,
        resultado: undefined,
        dentroPadrao: false,
        naoRealizado: false,
      }));

      form.setFieldsValue({
        ensaiosCertificado: ensaiosLimpos,
        resultadoUmidade: undefined,
      });
    }
  }, [amostraImpropria]);

  return (
    <>
      <Row gutter={[8, 8]}>
        <Col xs={24}>
          <Divider orientation="left">
            <Typography.Title level={screens.xs ? 5 : 4}>
              Ensaios
            </Typography.Title>
          </Divider>

          <Col span={24}>
            <Form.Item>
              <Button
                type="primary"
                icon={<OrderedListOutlined />}
                onClick={handleOpenModalHistorico}
                title="Histórico de Ensaios"
              >
                {screens.xs ? "" : "Histórico Análise"}
              </Button>
            </Form.Item>
          </Col>

          {exigeResultadoUmidade && (
            <Col span={24}>
              <Form.Item label="Resultado Umidade" name="resultadoUmidade">
                <InputNumber
                  onChange={handleChangeResultadoUmidade}
                  disabled={amostraImpropria}
                  style={{ width: "200px" }}
                  placeholder="Informe o resultado de umidade"
                  decimalSeparator=","
                />
              </Form.Item>
            </Col>
          )}

          <Table
            columns={columns}
            dataSource={ensaiosCertificado}
            pagination={false}
            size="small"
            bordered
            scroll={{ x: true }}
            className={styles.customTable}
          />
        </Col>

        {/* Campo hidden para validação do formulário */}
        <Form.Item name="ensaiosCertificado" style={{ display: "none" }}>
          <Input type="hidden" />
        </Form.Item>
      </Row>

      <ModalHistoricoAnalise
        open={isOpenModal}
        onClose={handleCloseModalHistorico}
        estabelecimentoId={estabelecimentoId}
        produtoId={produto?.id}
        naturezaAnalise={naturezaAnalise}
      />
    </>
  );
}
